import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {BehaviorSubject, Subject} from "rxjs/index";
import {Select, Store} from "@ngxs/store";
import {MatMenuTrigger} from "@angular/material/menu";
import {Dashboard, UniversityField, Visualization, WidgetIndicator} from "../../core/models";
import {FormBuilder, FormGroup} from "@angular/forms";
import {takeUntil} from "rxjs/operators";


interface Group {
  name: string,
  children: Array<UniversityField>
}

export interface IndicatorChangedEvent {
  index: number,
  value: Array<WidgetIndicator>,
  label: string
}


@Component({
  selector: 'app-indicator-select',
  templateUrl: './indicator-select.component.html',
  styleUrls: ['./indicator-select.component.scss']
})
export class IndicatorSelectComponent implements OnInit {

  @Input() index: number = 0;
  @Input() visualization: Visualization;
  @Input() value: WidgetIndicator;
  @Input() firstIndicator: WidgetIndicator;
  @Input() simpleMode: boolean;
  @Output() changed: EventEmitter<IndicatorChangedEvent> = new EventEmitter<IndicatorChangedEvent>();
  @Output() removed: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild(MatMenuTrigger) indicatorMenu: MatMenuTrigger;

  // refSubject$ = new BehaviorSubject(null);

  @Select((s) => s.core.universityFields) fields$;
  @Select((s) => s.core.countries) countries$;
  fields: Array<UniversityField>;

  destroyed$ = new Subject();

  indicator_name: string = '';
  label = '';
  showColor = false;
  showForm = false;
  showSplit = false;
  showUniX = true;
  showUniY = true;
  showRemoveBtn = false;
  showCheckboxes = true;
  options$ = new BehaviorSubject([]);

  groups: Array<Group> = [];
  form: FormGroup;

  constructor(private store: Store,
              private formBuilder: FormBuilder) {

    this.form = this.formBuilder.group({
      indicator: [''],
      indicator_type: [''],
      color: [true],
      form: [false],
      split: [false],
      uni_x: [false],
      uni_y: [false],
      rating_weight: [0],
      rating_method: [0],
      rating_null: [0],
      group2: [''],
      group2_color: [0],
      group2_form: [0],
    });

    this.form.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(v => {
        this.updateData();
        this.changed.emit({
          index: this.index,
          value: v,
          label: this.indicator_name,
        });
      });

    this.fields$.subscribe((v: Array<UniversityField>) => {
      this.fields = v;
      this.groups = this.getGroups(v);
    });
  }

  ngOnInit(): void {
    if(this.simpleMode) {
      this.showRemoveBtn = false;
      this.showCheckboxes = false;
    }
  }

  ngOnChanges(values) {
    let updateData = false;
    if (values.index && values.index.firstChange) {
      this.index = values.index.currentValue;
      updateData = true;
    }

    if (!this.simpleMode && values.value && values.value.firstChange) {
      this.value = values.value.currentValue;
      this.form.patchValue(this.value);
      updateData = true;
    }

    if (this.simpleMode && values.value && values.value.firstChange) {
      this.value = values.value.currentValue;
      this.form.patchValue({indicator: this.value});
      updateData = true;
    }

    if (values.visualization && values.visualization.firstChange) {
      this.visualization = values.visualization.currentValue;
      const options = this.getOptions();
      const val = this.form.get('indicator').value;

      // сбрасываю значение, ели его нет в списке доступных индикаторов
      if (! options.map(x => x.value).includes(val)) {
        this.form.patchValue({
          indicator: '',
          indicator_type: '',
          color: true,
          form: false,
          split: false,
          uni_x: false,
          uni_y: false,
          rating_weight: 0,
          rating_method: 0,
          rating_null: 0,
          group2: '',
          group2_color: 0,
          group2_form: 0,
        });
      }

      updateData = true;
    }

    if (values.firstIndicator) { // нужен только для Розы ветров
      this.firstIndicator = values.firstIndicator.currentValue;
      updateData = true;
    }

    if (updateData) {
      this.updateData();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  showMenu(event: MouseEvent) {
    event.preventDefault();
    this.indicatorMenu.openMenu();
  }

  updateData() {
    if(this.simpleMode) {
      this.label = 'Выберите параметр';
    } else {

      const type = this.controls['indicator_type'].value,
            visualization = this.visualization.value;
      let label = '';

      if (visualization) {
        label = this.visualization.labels[this.index] ? this.visualization.labels[this.index] : '';
      }

      this.label = label;
      this.showColor = ['cat', 'none'].includes(type) && visualization != 'branded_bar3';
      this.showForm = type == 'cat' && visualization == 'scatter';
      this.showSplit = type == 'cat' && !['wind_rose', 'radar'].includes(visualization);
      this.showUniX = type == 'cat' && !['bar', 'branded_bar3'].includes(visualization) && this.controls['split'].value;
      this.showUniY = type == 'cat' && !['bar', 'branded_bar3'].includes(visualization) && this.controls['split'].value;
      this.showRemoveBtn = !(['boxplot', 'violin'].includes(visualization) && this.index < 2);
    }

    this.indicator_name = this.getIndicatorName(this.form.value.indicator);
    const filteredFields = this.getOptions();
    const filteredGroups = this.getGroups(filteredFields);

    this.options$.next(filteredGroups);
  }

  get controls() {
    return this.form.controls;
  }

  remove() {
    this.removed.emit(this.index);
  }

  getOptions(): Array<UniversityField> {

    let f,
      f_empty = () => false,
      f1 = (v: UniversityField) =>  ['str', 'cat'].includes(v.type),
      f2 = (v: UniversityField) =>  ['int', 'float', 'money'].includes(v.type),
      f4 = (v: UniversityField) => !['str', 'cat'].includes(v.type),
      f5 = (v: UniversityField) =>  ['cat'].includes(v.type),
      f6 = (v: UniversityField) =>  ['cat', 'none'].includes(v.type);

    if(this.simpleMode) {
      return this.fields.filter(f2);
    }
    // if(this.visualization.value == 'bar') {
    //   f = this.index ? f5 : f2;
    // } else
    if(this.visualization.value == 'branded_bar1') {
      switch (this.index) {
        case 0: f = f1; break;
        case 1: f = f2; break;
        case 2: f = f4; break;
        default: f = f_empty; break;
      }
    } else if(this.visualization.value == 'branded_bar3') {
      f = f1;
    } else if(this.visualization.value.startsWith('branded_bar')) {
      f = f2;
    } else if (this.visualization.value == 'wind_rose') {
      if (this.index == 0) {
        return this.fields
          .filter((v: UniversityField) => v.type == 'score')
          .map((v: UniversityField) => v.group)
          .filter((item: string, i, ar) => ar.indexOf(item) === i)
          .map((group: string, i, ar) => new UniversityField(group, group, '', '', 'Рейтинг'));
        // return [
        //   new UniversityField("rating_factor", "Рейтинговый фактор", '', '', 'Рейтинг'),
        //   new UniversityField("rating_indicator", "Рейтинговый индикатор", '', '', 'Рейтинг'),
        // ];
      } else {
        f = (v: UniversityField) => v.group == this.firstIndicator.indicator;
        // } else if (this.firstIndicator.indicator == 'rating_factor') {
        //   f = (v: UniversityField) => v.type == 'score' && v.score_group == '';
        // } else {
        //   f = (v: UniversityField) => v.type == 'score' && v.score_group != '';
      }
    } else if (this.visualization.value == 'bar') {
      f = this.index ? f2 : f6;
    } else {
      f = this.index ? f2 : f6;
    }
    return this.fields.filter(f);
  }

  getGroups(v): Array<Group> {
    let groups = [];
    let indexes = {};
    for(let i=0, total = v.length ; i<total ; i++) {
      if (v[i].group == '') {
        continue;
      }
      if (indexes[v[i].group] == undefined) {
        indexes[v[i].group] = groups.length;
        groups.push({'name': v[i].group, 'children': []});
      }
      groups[indexes[v[i].group]]['children'].push(v[i]);
    }

    return groups;
  }

  selectIndicator(item: UniversityField) {
    this.indicatorMenu.closeMenu();

    if(this.form.value['indicator'] != item.value) {
      this.form.patchValue({
        indicator: item.value,
        // indicator_name: item.name,
        indicator_type: item.type,
      });
    }
  }

  getIndicatorName(value: string): string {
    // if (value == "rating_factor") {
    //   return "Рейтинговый фактор";
    // }
    // if (value == "rating_indicator") {
    //   return "Рейтинговый индикатор";
    // }

    const fields =  this.fields.filter(field => field.value ==  value);
    if (fields.length > 0) {
      return fields[0].name;
    }

    return value;
  }

}
